/**
 * Cart Parent-Child Relationship Handler
 * Handles deletion of parent items along with their child items
 */
(function() {
  'use strict';

  function getPropertyValue(item, propertyName) {
    if (!item.properties) return null;
    if (typeof item.properties === 'object' && !Array.isArray(item.properties)) {
      if (item.properties[propertyName] !== undefined) return item.properties[propertyName];
      const lowerKey = propertyName.toLowerCase();
      for (const key in item.properties) {
        if (key.toLowerCase() === lowerKey) return item.properties[key];
      }
      return null;
    }
    if (Array.isArray(item.properties)) {
      for (const prop of item.properties) {
        if (prop.name === propertyName || prop.first === propertyName) {
          return prop.value || prop.last || null;
        }
      }
    }
    return null;
  }

  async function removeParentAndChildren(associationKey, originalRemover) {
    try {
      const cartResponse = await fetch(`${window.theme.routes.cart}.js`);
      const cartData = await cartResponse.json();
      
      // Collect all line numbers to remove (parent + children)
      const linesToRemove = [];
      
      cartData.items.forEach((item, index) => {
        const lineNumber = index + 1;
        const itemAssociationKey = getPropertyValue(item, '_AssociationKey');
        const itemParentKey = getPropertyValue(item, '_ParentKey');
        
        // Parent item
        if (itemAssociationKey && String(itemAssociationKey).trim() === String(associationKey).trim()) {
          linesToRemove.push(lineNumber);
        }
        // Child item
        if (itemParentKey && String(itemParentKey).trim() === String(associationKey).trim()) {
          linesToRemove.push(lineNumber);
        }
      });
      
      // Fallback if nothing found - let original handler deal with it
      if (linesToRemove.length === 0) {
        return false;
      }
      
      // Remove from highest line number first to avoid index shifting
      linesToRemove.sort((a, b) => b - a);
      
      for (let i = 0; i < linesToRemove.length; i++) {
        const data = { line: linesToRemove[i], quantity: 0 };
        await fetch(`${window.theme.routes.cart}/change.js`, {
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        // Small delay between removals
        if (i < linesToRemove.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      // Verify and retry if needed
      const verifyResponse = await fetch(`${window.theme.routes.cart}.js`);
      const verifyCart = await verifyResponse.json();
      
      const remaining = verifyCart.items.filter((item) => {
        const itemAssociationKey = getPropertyValue(item, '_AssociationKey');
        const itemParentKey = getPropertyValue(item, '_ParentKey');
        return (itemAssociationKey && String(itemAssociationKey).trim() === String(associationKey).trim()) ||
               (itemParentKey && String(itemParentKey).trim() === String(associationKey).trim());
      });
      
      if (remaining.length > 0) {
        // Retry removal for remaining items
        const retryLines = [];
        verifyCart.items.forEach((item, index) => {
          const itemAssociationKey = getPropertyValue(item, '_AssociationKey');
          const itemParentKey = getPropertyValue(item, '_ParentKey');
          if ((itemAssociationKey && String(itemAssociationKey).trim() === String(associationKey).trim()) ||
              (itemParentKey && String(itemParentKey).trim() === String(associationKey).trim())) {
            retryLines.push(index + 1);
          }
        });
        retryLines.sort((a, b) => b - a);
        for (const line of retryLines) {
          await fetch(`${window.theme.routes.cart}/change.js`, {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ line, quantity: 0 }),
          });
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      // Get final cart state
      const finalResponse = await fetch(`${window.theme.routes.cart}.js`);
      const finalCart = await finalResponse.json();
      
      // Fire cart change event
      document.dispatchEvent(new CustomEvent('theme:cart:change', {
        detail: { cart: finalCart },
        bubbles: true,
      }));
      
      // Trigger cart reload - this is what the theme listens for
      document.dispatchEvent(new CustomEvent('theme:cart:reload'));
      
      // Also manually update the cart HTML as backup
      await refreshCartHTML(finalCart);
      
      return true;
    } catch (error) {
      console.error('Error removing parent and children:', error);
      return false;
    }
  }

  async function refreshCartHTML(cart) {
    try {
      // Fetch the updated cart items section
      const response = await fetch(`${window.theme.routes.root_url}?section_id=api-cart-items`);
      const html = await response.text();
      
      // Update line items container
      const lineItemsContainer = document.querySelector('[data-line-items]');
      if (lineItemsContainer) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContent = doc.querySelector('[data-api-content]');
        if (newContent) {
          lineItemsContainer.innerHTML = newContent.innerHTML;
        }
      }
      
      // Update cart count/bubble
      const cartCountResponse = await fetch(`${window.theme.routes.root_url}?section_id=api-cart-count`);
      const cartCountHtml = await cartCountResponse.text();
      const cartBubbles = document.querySelectorAll('[data-cart-count]');
      cartBubbles.forEach(bubble => {
        bubble.textContent = cart.item_count;
      });
      
      // Update cart total
      const cartFinal = document.querySelectorAll('[data-cart-final]');
      cartFinal.forEach(el => {
        if (window.theme && window.theme.moneyFormat) {
          el.innerHTML = formatMoney(cart.total_price, window.theme.moneyFormat);
        } else {
          el.innerHTML = '$' + (cart.total_price / 100).toFixed(2);
        }
      });
      
      // Hide loading states
      document.querySelectorAll('[data-item-loadbar]').forEach(el => {
        el.style.display = 'none';
      });
      document.querySelectorAll('[data-cart-loading]').forEach(el => {
        el.classList.remove('cart--loading');
      });
      
      // Show empty state if cart is empty
      if (cart.item_count === 0) {
        document.querySelectorAll('[data-cart-empty]').forEach(el => {
          el.classList.remove('cart--hidden');
        });
        document.querySelectorAll('[data-cart-form]').forEach(el => {
          el.classList.add('cart--hidden');
        });
        document.querySelectorAll('[data-cart-bottom]').forEach(el => {
          el.classList.add('cart--hidden');
        });
        document.querySelectorAll('[data-cart-bar]').forEach(el => {
          el.classList.add('cart--hidden');
        });
      }
    } catch (e) {
      console.error('Error refreshing cart HTML:', e);
      // Last resort: reload the page
      window.location.reload();
    }
  }
  
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = (cents / 100).toFixed(2);
    return format ? format.replace(/\{\{\s*amount\s*\}\}/, value) : '$' + value;
  }

  function initParentChildRemoval() {
    // Use event delegation to handle dynamically added remove buttons
    document.addEventListener('click', async function(e) {
      const remover = e.target.closest('[data-remove-key][data-is-parent="true"]');
      if (!remover) return;
      
      const parentKey = remover.getAttribute('data-parent-key');
      if (!parentKey) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      // Show loading state
      const cartItem = remover.closest('[data-cart-item]');
      const loadbar = cartItem?.querySelector('[data-item-loadbar]');
      if (loadbar) loadbar.style.display = 'block';
      
      const loader = document.querySelector('[data-cart-loading]');
      if (loader) loader.classList.add('cart--loading');
      
      const success = await removeParentAndChildren(parentKey, remover);
      
      if (!success) {
        // Hide loading states before fallback
        if (loadbar) loadbar.style.display = 'none';
        if (loader) loader.classList.remove('cart--loading');
        // Fallback: trigger original click behavior by following the href
        window.location.href = remover.href;
      }
    }, true); // Use capture phase to intercept before theme.js
  }

  // Initialize on DOM ready and after AJAX cart updates
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initParentChildRemoval);
  } else {
    initParentChildRemoval();
  }
})();